import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { applyPublicTenant } from '@/utils/publicTenant';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Wrench, Loader2, CalendarClock, CheckCircle2, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WD = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const fmtDate = (iso) => {
  try { const d = new Date(iso + 'T00:00:00'); return `${WD[d.getDay()]}, ${d.toLocaleDateString('de-CH')}`; }
  catch { return iso; }
};
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const addDays = (iso, n) => { const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const PublicWorkshopBooking = () => {
  const params = new URLSearchParams(window.location.search);
  const service = params.get('service') || 'reifenwechsel';

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [date, setDate] = useState(todayIso());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', plate: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    applyPublicTenant();
    (async () => {
      try {
        const r = await axios.get(`${API}/public/workshop/booking-info`, { params: { service } });
        setInfo(r.data);
        // Vorauswahl: erster offener Tag ab heute
        const oh = r.data?.opening_hours || {};
        const wkKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
        let pick = todayIso();
        for (let i = 0; i < 14; i++) {
          const cand = addDays(todayIso(), i);
          const wd = new Date(cand + 'T00:00:00').getDay();
          if ((oh[wkKeys[wd]] || []).length > 0) { pick = cand; break; }
        }
        setDate(pick);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Buchung konnte nicht geladen werden.');
      } finally { setLoading(false); }
    })();
  }, [service]);

  const loadSlots = useCallback(async (d) => {
    setSlotsLoading(true); setSlot('');
    try {
      const r = await axios.get(`${API}/public/workshop/availability`, { params: { date: d, service } });
      setSlots(r.data?.slots || []);
    } catch { setSlots([]); }
    finally { setSlotsLoading(false); }
  }, [service]);

  useEffect(() => { if (info && info.booking_enabled) loadSlots(date); }, [info, date, loadSlots]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Bitte Ihren Namen angeben'); return; }
    if (!slot) { toast.error('Bitte eine Uhrzeit wählen'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/workshop/book`, { service, date, time: slot, ...form });
      setDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Buchung fehlgeschlagen');
      if (e?.response?.status === 409) loadSlots(date);
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-7 w-7 animate-spin text-orange-500" /></div>;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white rounded-2xl shadow-sm border p-8 text-center max-w-md">
        <Wrench className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-600">{error}</p>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white rounded-2xl shadow-sm border p-8 text-center max-w-md" data-testid="booking-success">
        <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-xl font-semibold text-slate-800">Termin gebucht!</h1>
        <p className="text-slate-500 mt-2">{done.service_label} am <b>{fmtDate(done.date)}</b> um <b>{done.time} Uhr</b>.</p>
        <p className="text-slate-400 text-sm mt-4">{done.message}</p>
        <p className="text-slate-400 text-xs mt-2" data-testid="booking-confirmation-note">Eine Bestätigung mit allen Angaben wurde an Sie gesendet.</p>
      </div>
    </div>
  );

  const maxDate = info ? addDays(todayIso(), info.max_advance_days || 60) : addDays(todayIso(), 60);

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded-xl bg-orange-600 flex items-center justify-center text-white"><Wrench className="h-5 w-5" /></div>
          <div>
            <div className="text-xs uppercase tracking-wider text-orange-600 font-semibold">{info?.practice || 'Werkstatt'}</div>
            <h1 className="text-2xl font-bold text-slate-800 leading-tight">{info?.service_label || 'Termin'} buchen</h1>
          </div>
        </div>

        {!info?.booking_enabled ? (
          <div className="bg-white rounded-2xl shadow-sm border p-8 text-center text-slate-500">
            Online-Buchung ist derzeit nicht verfügbar. Bitte rufen Sie uns an.
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border p-6 space-y-6">
            {/* Datum */}
            <div>
              <Label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><CalendarClock className="h-4 w-4 text-orange-600" />Datum</Label>
              <Input type="date" value={date} min={todayIso()} max={maxDate} onChange={(e) => setDate(e.target.value)} data-testid="booking-date" className="w-full" />
              <p className="text-xs text-slate-400 mt-1">{fmtDate(date)} · ca. {info?.duration_minutes} Min. pro Termin</p>
            </div>

            {/* Uhrzeit */}
            <div>
              <Label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><Clock className="h-4 w-4 text-orange-600" />Uhrzeit</Label>
              {slotsLoading ? (
                <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin text-orange-500 mx-auto" /></div>
              ) : slots.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center bg-slate-50 rounded-lg" data-testid="booking-no-slots">An diesem Tag sind keine Termine verfügbar. Bitte wählen Sie einen anderen Tag.</p>
              ) : (
                <div className="grid grid-cols-4 gap-2" data-testid="booking-slots">
                  {slots.map((s) => (
                    <button
                      key={s.time}
                      disabled={!s.available}
                      data-testid={`booking-slot-${s.time}`}
                      onClick={() => setSlot(s.time)}
                      className={`py-2 rounded-lg text-sm font-medium border transition-colors ${
                        slot === s.time ? 'bg-orange-600 text-white border-orange-600'
                          : s.available ? 'bg-white text-slate-700 border-slate-200 hover:border-orange-400'
                          : 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed line-through'}`}
                    >{s.time}</button>
                  ))}
                </div>
              )}
            </div>

            {/* Kontakt */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-sm text-slate-600">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="booking-name" placeholder="Vor- und Nachname" />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Telefon</Label>
                <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="booking-phone" placeholder="079 ..." />
              </div>
              <div>
                <Label className="text-sm text-slate-600">E-Mail</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="booking-email" placeholder="name@email.ch" />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Kennzeichen</Label>
                <Input value={form.plate} onChange={(e) => setForm((f) => ({ ...f, plate: e.target.value }))} data-testid="booking-plate" placeholder="z. B. ZH 123456" />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Bemerkung</Label>
                <Textarea rows={1} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="booking-note" placeholder="optional" />
              </div>
            </div>

            <Button onClick={submit} disabled={submitting || !slot} data-testid="booking-submit" className="w-full bg-orange-600 hover:bg-orange-700 h-11 text-base">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (slot ? `Termin am ${fmtDate(date)} um ${slot} buchen` : 'Bitte Uhrzeit wählen')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicWorkshopBooking;
